/** @format */

import {
	ChangeDetectionStrategy,
	Component,
	effect,
	inject,
	input,
	makeStateKey,
	OnInit,
	signal,
	TransferState,
	viewChild,
	ViewContainerRef
} from '@angular/core';

import { Store } from '@ngrx/store';

import { setHomrBannerData } from '@plp/data-access/state';
import { catchError, of, take } from 'rxjs';
import { ComponentBindings } from './homr-banner-modules';
import { DynamicTemplateLoader } from '@shared/data-access/services';
import { BannerInfo, HomrDataService } from '@shared/homr/data-access';
import { ProductListSEO } from '@product-recommendations/data-access';
import { ActivatedRoute } from '@angular/router';

@Component({
	selector: 'plp-homr-components-template',
	templateUrl: './homr-template.component.html',
	styleUrl: './homr-template.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomrTemplateComponent implements OnInit {
	private readonly homrDataService = inject(HomrDataService);
	private readonly store = inject(Store);
	private readonly dynamicTemplateLoader = inject(DynamicTemplateLoader);
	private readonly transferState = inject(TransferState);
	private readonly bannerInfoState = makeStateKey<BannerInfo | null | false>(
		'HomrBannerTemplateInfo'
	);
	private readonly initialBannerInfo = this.transferState.get(this.bannerInfoState, null);
	private readonly route = inject(ActivatedRoute);
	private readonly isSRLP = this.route.snapshot.url
		.map((segment) => segment.path)[0]
		?.startsWith('search');

	chainAbbr = input('');
	productListSEOData = input<ProductListSEO>();
	bannerInfo = signal<BannerInfo | null | false>(this.isSRLP ? false : this.initialBannerInfo);
	components: Record<string, Function>;
	contentLoaded = signal(false);
	templateOutlet = viewChild('templateOutlet', { read: ViewContainerRef });

	constructor() {
		this.components = ComponentBindings();

		effect(() => {
			this.loadTemplate(this.bannerInfo());
		});
	}

	ngOnInit() {
		this.homrDataService
			.getData$('banner', 2, this.productListSEOData()?.seoUrl)
			.pipe(
				catchError(() => of(null)),
				take(1)
			)
			.subscribe((bannerInfo?: BannerInfo) => {
				if (bannerInfo) {
					this.store.dispatch(setHomrBannerData({ homrBannerData: bannerInfo }));
					this.bannerInfo.set(bannerInfo);
					this.transferState.set(this.bannerInfoState, null);
				} else {
					this.bannerInfo.set(false);
					this.contentLoaded.set(true);
					this.transferState.set(this.bannerInfoState, false);
				}
			});
	}

	async loadTemplate(bannerInfo: BannerInfo | null | false) {
		if (bannerInfo && this.templateOutlet()) {
			const keys = Object.keys(bannerInfo.content);
			let loaded = false;

			for (const k in keys) {
				if (bannerInfo.content[keys[k]]) {
					const key = keys[k];
					const data = bannerInfo.content[key];
					if (this.components[key]) {
						await this.components[key](
							this.dynamicTemplateLoader,
							data,
							this.templateOutlet(),
							{
								tagSectionNum: +k + 1,
								widgetTypeTag: key,
								chainAbbr: this.chainAbbr()
							}
						);

						loaded = true;
					}
				}
			}

			this.contentLoaded.set(loaded);
		}
	}
}
